import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SyncIcon from '@mui/icons-material/Sync';
import api from '../../api';

interface Props {
  leadId: string | undefined;
  leadStatus: string | undefined;
  onSuccess?: () => void;
  variant?: 'outlined' | 'text' | 'contained';
}

const ALLOWED_STATUSES = new Set(['Draft', 'Working Lead', 'Updated']);

/**
 * Lead-level utility button. Calls POST /workloading/lead/:id/recompute-rates
 * which re-runs TechnicianCoreService.getMediaRateBySkills() for every
 * LeadServiceTask on the bid and writes the fresh median back.
 *
 * Hides on non-eligible lead statuses (Converted / In-Negotiation / Rejected)
 * so operators can't accidentally mutate a proposal already sent to a client.
 * Renders a success/error message inline after the action.
 */
const RecomputeRatesButtonDefaults = {
  onSuccess: undefined,
  variant: 'outlined' as const
};
const RecomputeRatesButton: React.FC<Props> = function RecomputeRatesButton({
  leadId,
  leadStatus,
  onSuccess,
  variant
}) {
  const [inFlight, setInFlight] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss the floating message: 5s on success, 10s on error so
  // the operator has time to read the failure. Cleared on unmount or
  // when a new message replaces it.
  useEffect(() => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    if (message) {
      dismissTimer.current = setTimeout(
        () => setMessage(null),
        error ? 10_000 : 5_000
      );
    }
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, [message, error]);

  if (!leadId || !ALLOWED_STATUSES.has(String(leadStatus ?? ''))) return null;

  const run = async () => {
    setInFlight(true);
    setMessage(null);
    setError(false);
    try {
      const result = await api.WorkloadingApi.recomputeLeadRates(leadId);
      const mediaCount = result.mediaRateChanges.length;
      const techCount = result.technicianRateChanges.length;

      if (mediaCount === 0 && techCount === 0) {
        setMessage(
          `Checked ${result.tasksInspected} task(s) — all rates already match current staff.`
        );
      } else {
        const parts: string[] = [];
        if (techCount > 0) {
          const sample = result.technicianRateChanges
            .slice(0, 2)
            .map(
              (c) =>
                `${c.technicianName} ($${c.oldRate.toFixed(2)} → $${c.newRate.toFixed(2)})`
            )
            .join(', ');
          const more =
            techCount > 2 ? ` +${techCount - 2} more` : '';
          parts.push(
            `${techCount} technician rate(s) refreshed: ${sample}${more}`
          );
        }
        if (mediaCount > 0) {
          parts.push(
            `${mediaCount} task median(s) updated (display tile only)`
          );
        }
        setMessage(
          `${parts.join('; ')}. Refresh the Analysis tab to see updated Keep cost.`
        );
      }
      onSuccess?.();
    } catch (err: any) {
      setError(true);
      setMessage(
        err?.response?.data?.message ??
          err?.message ??
          'Recompute failed. Try again or check lead status.'
      );
    } finally {
      setInFlight(false);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip
        arrow
        placement="bottom"
        title={
          inFlight
            ? 'Refreshing rates from current staff…'
            : 'Refresh technician rates from current staff. Pulls live rates from the People module into this lead.'
        }
      >
        <span>
          <IconButton
            disabled={inFlight}
            onClick={run}
            size="small"
            sx={{
              '&:hover': { bgcolor: 'action.hover' },
              borderColor: 'divider'
            }}
          >
            {inFlight ? (
              <CircularProgress size={18} />
            ) : (
              <SyncIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      {message && (
        <Box
          sx={{
            alignItems: 'flex-start',
            bgcolor: 'common.white',
            border: '1px solid',
            borderColor: error ? 'error.main' : 'divider',
            borderRadius: 1,
            boxShadow: 2,
            display: 'flex',
            gap: 1,
            maxWidth: 360,
            mt: 1,
            p: 1.5,
            position: 'absolute',
            right: 0,
            top: '100%',
            zIndex: 10
          }}
        >
          <Typography
            color={error ? 'error.main' : 'success.main'}
            sx={{ flex: 1 }}
            variant="body_100"
          >
            {message}
          </Typography>
          <IconButton
            aria-label="Dismiss"
            onClick={() => setMessage(null)}
            size="small"
            sx={{ mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};
RecomputeRatesButton.defaultProps = RecomputeRatesButtonDefaults;

export default RecomputeRatesButton;
